import { ContactSummary } from "./ContactSummary";
import { Chat } from "./Chat";
import type { EntityListScope } from "./Chat";
import { useCondos } from "context/CondosContext";
import { useBoardMembers } from "context/BoardMembersContext";
import { useResidents } from "context/ResidentsContext";
import { useWorkers } from "context/WorkersContext";
import { useProviders } from "context/ProvidersContext";
import { usePendingContacts } from "context/PendingContactsContext";
import { useManagers } from "context/ManagersContext";
import { useOtherContacts } from "context/OtherContactsContext";
import { useAuth } from "context/AuthContext";
import { useEffect, useMemo, useRef, useState } from "react";
import fetchWithCredentials from "apiClient";
import type { ReplyMute } from "types/ReplyMute";
import {
  otherContactListSubtitle,
  pendingContactListSubtitle,
  providerListSubtitle,
  boardMemberListSubtitle,
  residentListSubtitle,
  workerListSubtitle,
} from "utils/contactListSubtitle";

interface ContactPanelProps {
  contactId: number;
  name: string;
  phone: string;
  landlinePhone: string;
  mail: string;
  wappPhoneInvalid?: boolean;
  messageId?: string | null;
  lastReadAt?: string | null;
  residentId?: number;
  boardMemberId?: number;
  isResidentActive?: boolean;
  workerId?: number;
  isWorkerActive?: boolean;
  isWorkerOnCurrentCondo?: boolean;
  providerId?: number;
  pendingContactId?: number;
  isProviderOnCurrentCondo?: boolean;
  condoId?: number;
  onEntitiesMaybeChanged?: (entity: EntityListScope) => void;
}

export function ContactPanel({
  contactId,
  name,
  phone,
  landlinePhone,
  mail,
  wappPhoneInvalid = false,
  messageId,
  lastReadAt,
  residentId,
  boardMemberId,
  isResidentActive,
  workerId,
  isWorkerActive,
  isWorkerOnCurrentCondo,
  providerId,
  pendingContactId,
  isProviderOnCurrentCondo,
  condoId,
  onEntitiesMaybeChanged,
}: ContactPanelProps) {
  const { selectedCondoMode, condos, selectedCondoId } = useCondos();
  const { residents, updateLastReadAt: updateResidentLastReadAt, refresh } =
    useResidents();
  const {
    boardMembers,
    updateLastReadAt: updateBoardMemberLastReadAt,
    refresh: refreshBoardMembers,
  } = useBoardMembers();
  const { workers, otherCondoWorkers, updateLastReadAt: updateWorkerLastReadAt, refresh: refreshWorkers } =
    useWorkers();
  const {
    providers,
    otherCondoProviders,
    updateLastReadAt: updateProviderLastReadAt,
    refresh: refreshProviders,
  } = useProviders();
  const {
    pendingContacts,
    updateLastReadAt: updatePendingContactLastReadAt,
    refresh: refreshPendingContacts,
    removePendingContact,
  } = usePendingContacts();
  const {
    otherContacts,
    updateLastReadAt: updateOtherContactLastReadAt,
    refresh: refreshOtherContacts,
  } = useOtherContacts();
  const { managers, refresh: refreshManagers } = useManagers();
  const { managerId, lang } = useAuth();
  const previousContactIdRef = useRef<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [autoResponseLoading, setAutoResponseLoading] = useState(false);
  const [contactAutoResponseEnabled, setContactAutoResponseEnabled] = useState(true);
  const [resumeRepliesLoading, setResumeRepliesLoading] = useState(false);

  const selectedManager =
    managers.find((m) => m.contact.id === contactId) || null;
  const currentManager = managers.find((m) => m.id === managerId) || null;
  const isRegistrableContact = Boolean(residentId || workerId || providerId);
  const showManagerAutoResponseToggle = Boolean(selectedManager?.wappPhoneNumberId);
  const showContactAutoResponseToggle =
    isRegistrableContact && Boolean(currentManager?.wappPhoneNumberId);
  const showAutoResponseMenuAction =
    showManagerAutoResponseToggle || showContactAutoResponseToggle;
  const autoResponseVariant = showManagerAutoResponseToggle ? "self" : "contact";
  const autoResponseEnabled = showManagerAutoResponseToggle
    ? Boolean(selectedManager?.wappAutoResponseEnabled)
    : contactAutoResponseEnabled;
  const selectedCondo = condos.find((c) => c.id === selectedCondoId);
  const condoName =
    selectedCondo?.name ??
    (selectedManager ? selectedManager.propertyManagerName : undefined);
  const isManagerContact = Boolean(selectedManager);
  const locale = lang === "es" ? "es" : "en";
  const roleLabel = boardMemberId
    ? locale === "es"
      ? "Comisión"
      : "Board"
    : residentId
    ? locale === "es"
      ? "Residente"
      : "Resident"
    : workerId
      ? locale === "es"
        ? "Empleado"
        : "Worker"
      : providerId
        ? locale === "es"
          ? "Proveedor"
          : "Provider"
        : pendingContactId
          ? locale === "es"
            ? "Pendiente"
            : "Pending"
          : isManagerContact
            ? locale === "es"
              ? "Administración"
              : "Administration"
            : undefined;

  const contextLines = useMemo(() => {
    const lines: string[] = [];

    if (boardMemberId != null) {
      const member = boardMembers.find((row) => row.id === boardMemberId);
      if (member) {
        lines.push(boardMemberListSubtitle(member, locale));
      }
    } else if (residentId != null) {
      const resident = residents.find((r) => r.id === residentId);
      if (resident) {
        lines.push(residentListSubtitle(resident, locale));
      }
    } else if (workerId != null) {
      const worker =
        workers.find((w) => w.id === workerId) ||
        otherCondoWorkers.find((w) => w.id === workerId);
      if (worker) {
        lines.push(workerListSubtitle(worker, locale));
        if (isWorkerOnCurrentCondo === false) {
          lines.push(locale === "es" ? "Otro edificio" : "Other building");
        }
      }
    } else if (providerId != null) {
      const provider =
        providers.find((p) => p.id === providerId) ||
        otherCondoProviders.find((p) => p.id === providerId);
      if (provider) {
        const companyName = provider.name.trim();
        if (companyName && companyName !== name.trim()) {
          lines.push(companyName);
        }
        const subtitle = providerListSubtitle(provider);
        if (subtitle) {
          lines.push(subtitle);
        }
        if (isProviderOnCurrentCondo === false) {
          lines.push(locale === "es" ? "Otro edificio" : "Other building");
        }
      }
    } else if (pendingContactId != null) {
      const pending = pendingContacts.find((p) => p.id === pendingContactId);
      if (pending) {
        const subtitle = pendingContactListSubtitle(pending);
        if (subtitle) {
          lines.push(subtitle);
        }
      }
    } else if (selectedManager?.propertyManagerName) {
      lines.push(selectedManager.propertyManagerName);
    } else if (
      otherContacts.some((row) => row.contact.id === contactId)
    ) {
      lines.push(otherContactListSubtitle(locale));
    }

    return lines;
  }, [
    boardMemberId,
    boardMembers,
    residentId,
    residents,
    workerId,
    workers,
    otherCondoWorkers,
    isWorkerOnCurrentCondo,
    providerId,
    providers,
    otherCondoProviders,
    isProviderOnCurrentCondo,
    pendingContactId,
    pendingContacts,
    selectedManager,
    otherContacts,
    contactId,
    name,
    locale,
  ]);

  const replyMute = useMemo((): ReplyMute | null => {
    const candidates: Array<ReplyMute | null | undefined> = [
      residents.find((row) => row.contact.id === contactId)?.replyMute,
      boardMembers.find((row) => row.contact.id === contactId)?.replyMute,
      workers.find((row) => row.contact.id === contactId)?.replyMute,
      otherCondoWorkers.find((row) => row.contact.id === contactId)?.replyMute,
      providers.find((row) => row.contact.id === contactId)?.replyMute,
      otherCondoProviders.find((row) => row.contact.id === contactId)?.replyMute,
      pendingContacts.find((row) => row.contact.id === contactId)?.replyMute,
      otherContacts.find((row) => row.contact.id === contactId)?.replyMute,
    ];
    return candidates.find((mute) => mute) ?? null;
  }, [
    contactId,
    residents,
    boardMembers,
    workers,
    otherCondoWorkers,
    providers,
    otherCondoProviders,
    pendingContacts,
    otherContacts,
  ]);

  const handleResumeReplies = async () => {
    setResumeRepliesLoading(true);
    try {
      await fetchWithCredentials(
        `/manager-api/contacts/${contactId}/resume-replies/`,
        { method: "POST" },
      );
      if (condoId != null) {
        await Promise.all([
          refresh(condoId),
          refreshBoardMembers(condoId),
          refreshWorkers(condoId),
          refreshProviders(condoId),
          refreshPendingContacts(condoId),
        ]);
      }
      await refreshOtherContacts();
    } catch (err) {
      console.error("Error resuming Eloria replies:", err);
    } finally {
      setResumeRepliesLoading(false);
    }
  };

  useEffect(() => {
    if (!showContactAutoResponseToggle) {
      setContactAutoResponseEnabled(true);
      return;
    }

    let cancelled = false;

    async function fetchContactAutoResponse() {
      try {
        const data = await fetchWithCredentials(
          `/manager-api/contacts/${contactId}/auto-response/`
        );
        if (!cancelled) {
          setContactAutoResponseEnabled(Boolean(data.autoResponseEnabled));
        }
      } catch (err) {
        console.error("Error fetching contact auto-response setting:", err);
      }
    }

    void fetchContactAutoResponse();

    return () => {
      cancelled = true;
    };
  }, [contactId, showContactAutoResponseToggle]);

  useEffect(() => {
    if (contactId && contactId !== previousContactIdRef.current) {
      previousContactIdRef.current = contactId;

      const now = new Date().toISOString();

      // Try to update in all contexts - the one that has this contact will update
      updateResidentLastReadAt(contactId, now);
      updateBoardMemberLastReadAt(contactId, now);
      updateWorkerLastReadAt(contactId, now);
      updateProviderLastReadAt(contactId, now);
      updatePendingContactLastReadAt(contactId, now);
      updateOtherContactLastReadAt(contactId, now);
    }
  }, [
    contactId,
    updateResidentLastReadAt,
    updateBoardMemberLastReadAt,
    updateWorkerLastReadAt,
    updateProviderLastReadAt,
    updatePendingContactLastReadAt,
    updateOtherContactLastReadAt,
  ]);

  const handleDeactivate = async () => {
    if (!condoId || actionLoading) return;
    setActionLoading(true);
    try {
      if (residentId) {
        await fetchWithCredentials(
          `/manager-api/residents/${residentId}/deactivate/?condo=${condoId}`,
          { method: "POST" }
        );
        await Promise.all([refresh(condoId), refreshBoardMembers(condoId)]);
      } else if (workerId) {
        await fetchWithCredentials(
          `/manager-api/workers/${workerId}/deactivate/?condo=${condoId}`,
          { method: "POST" }
        );
        await refreshWorkers(condoId);
      }
    } catch (err) {
      console.error("Error deactivating contact:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveFromBoard = async () => {
    if (!condoId || boardMemberId == null || actionLoading) return;
    setActionLoading(true);
    try {
      await fetchWithCredentials(
        `/manager-api/board-members/${boardMemberId}/remove/?condo=${condoId}`,
        { method: "POST" }
      );
      await refreshBoardMembers(condoId);
    } catch (err) {
      console.error("Error removing board member:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!condoId || actionLoading) return;
    setActionLoading(true);
    try {
      if (residentId) {
        await fetchWithCredentials(
          `/manager-api/residents/${residentId}/activate/?condo=${condoId}`,
          { method: "POST" }
        );
        await refresh(condoId);
      } else if (workerId) {
        await fetchWithCredentials(
          `/manager-api/workers/${workerId}/activate/?condo=${condoId}`,
          { method: "POST" }
        );
        await refreshWorkers(condoId);
      }
    } catch (err) {
      console.error("Error activating contact:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddWorkerToCondo = async () => {
    if (!workerId || !condoId || actionLoading) return;
    setActionLoading(true);
    try {
      await fetchWithCredentials(
        `/manager-api/workers/${workerId}/attach/?condo=${condoId}`,
        { method: "POST" }
      );
      await refreshWorkers(condoId);
    } catch (err) {
      console.error("Error adding worker to building:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddProviderToCondo = async () => {
    if (!providerId || !condoId || actionLoading) return;
    setActionLoading(true);
    try {
      await fetchWithCredentials(
        `/manager-api/providers/${providerId}/attach/?condo=${condoId}`,
        { method: "POST" }
      );
      await refreshProviders(condoId);
    } catch (err) {
      console.error("Error adding provider to building:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveProviderFromCondo = async () => {
    if (!providerId || !condoId || actionLoading) return;
    setActionLoading(true);
    try {
      await fetchWithCredentials(
        `/manager-api/providers/${providerId}/detach/?condo=${condoId}`,
        { method: "POST" }
      );
      await refreshProviders(condoId);
    } catch (err) {
      console.error("Error removing provider from building:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAutoResponse = async (enabled: boolean) => {
    if (autoResponseLoading) return;
    setAutoResponseLoading(true);
    try {
      if (showManagerAutoResponseToggle && selectedManager) {
        await fetchWithCredentials(
          `/manager-api/managers/${selectedManager.id}/auto-response/`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ wapp_auto_response_enabled: enabled }),
          }
        );
        await refreshManagers();
      } else if (showContactAutoResponseToggle) {
        await fetchWithCredentials(
          `/manager-api/contacts/${contactId}/auto-response/`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ auto_response_enabled: enabled }),
          }
        );
        setContactAutoResponseEnabled(enabled);
      }
    } catch (err) {
      console.error("Error toggling auto response:", err);
    } finally {
      setAutoResponseLoading(false);
    }
  };

  const handleAcceptPendingContact = async () => {
    if (!pendingContactId || !condoId || actionLoading) return;
    setActionLoading(true);
    removePendingContact(pendingContactId);
    try {
      await fetchWithCredentials(
        `/manager-api/pending-contacts/${pendingContactId}/confirm/?condo=${condoId}`,
        { method: "POST" }
      );
    } catch (err) {
      console.error("Error confirming pending contact:", err);
      await refreshPendingContacts(condoId);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPendingContact = async () => {
    if (!pendingContactId || !condoId || actionLoading) return;
    setActionLoading(true);
    removePendingContact(pendingContactId);
    try {
      await fetchWithCredentials(
        `/manager-api/pending-contacts/${pendingContactId}/reject/?condo=${condoId}`,
        { method: "POST" }
      );
    } catch (err) {
      console.error("Error rejecting pending contact:", err);
      await refreshPendingContacts(condoId);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-2 lg:gap-3">
      <div className="shrink-0">
        <ContactSummary
          name={name}
          phone={phone}
          landlinePhone={landlinePhone}
          mail={mail}
          wappPhoneInvalid={wappPhoneInvalid}
          condoName={condoName}
          roleLabel={roleLabel}
          contextLines={contextLines}
          residentId={residentId}
          boardMemberId={boardMemberId}
          onRemoveFromBoard={
            boardMemberId != null ? handleRemoveFromBoard : undefined
          }
          isResidentActive={isResidentActive}
          workerId={workerId}
          isWorkerActive={isWorkerActive}
          isWorkerOnCurrentCondo={isWorkerOnCurrentCondo}
          providerId={providerId}
          isProviderOnCurrentCondo={isProviderOnCurrentCondo}
          condoId={condoId}
          actionLoading={actionLoading}
          onDeactivate={handleDeactivate}
          onActivate={handleActivate}
          onAddWorkerToCondo={handleAddWorkerToCondo}
          onAddProviderToCondo={handleAddProviderToCondo}
          onRemoveProviderFromCondo={handleRemoveProviderFromCondo}
          pendingContactId={pendingContactId}
          onAcceptPendingContact={handleAcceptPendingContact}
          onRejectPendingContact={handleRejectPendingContact}
          showAutoResponseMenuAction={showAutoResponseMenuAction}
          showAutoResponseToggleStrip={showManagerAutoResponseToggle}
          autoResponseEnabled={autoResponseEnabled}
          autoResponseLoading={autoResponseLoading}
          onToggleAutoResponse={handleToggleAutoResponse}
          autoResponseVariant={autoResponseVariant}
          workerDisturbSchedule={
            workerId != null
              ? (
                  workers.find((w) => w.id === workerId) ||
                  otherCondoWorkers.find((w) => w.id === workerId)
                )?.disturbSchedule ?? null
              : null
          }
          replyMute={replyMute}
          resumeRepliesLoading={resumeRepliesLoading}
          onResumeReplies={handleResumeReplies}
          onWorkerDisturbScheduleSaved={async () => {
            if (condoId != null) {
              await refreshWorkers(condoId);
            }
          }}
        />
      </div>
      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
        <Chat
          key={`${contactId}-${selectedCondoMode}`}
          contactId={contactId}
          contactName={name}
          condoMode={selectedCondoMode}
          messageId={messageId}
          lastReadAt={lastReadAt}
          onEntitiesMaybeChanged={onEntitiesMaybeChanged}
        />
      </div>
    </div>
  );
}
